import type { QueryClient } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';
import { create } from 'zustand';
import {
  combine,
  createJSONStorage,
  persist,
  subscribeWithSelector,
} from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

import {
  EXTERNAL_CLUSTER_QUERY_KEY,
  EXTERNAL_CLUSTERS_QUERY_KEY,
  INVENTORY_ASSET_LIST_CLUSTERS_QUERY_KEY,
  useCluster,
} from '@cast/shared-cluster-context-data-access';
import { isSimulatedDemoClusterId, makeLogger } from '@cast/utils';

import {
  subscribeAbilityState,
  ORGANIZATION_SETTINGS_QUERY_KEY,
} from '@cast/infrastructure-abilities';
import { ORGANIZATIONS_QUERY_KEY } from '@cast/infrastructure-organization';

type DemoState = {
  // Raw facts from API responses
  hasClusters: boolean | null; // null = not yet determined
  hasDiscoveredClusters: boolean | null; // null = not yet determined
  hasEnabledSecurityAgents: boolean | null; // null = not yet determined

  // User-initiated demo mode
  isLlmDemoMode: boolean;
  isUserTriggeredDemoMode: boolean;
};

const initialState: DemoState = {
  hasClusters: null,
  hasDiscoveredClusters: null,
  hasEnabledSecurityAgents: null,
  isLlmDemoMode: false,
  isUserTriggeredDemoMode: false,
};

// Pure helper functions for computing demo modes
// These are used both in store methods and subscription callbacks
type DemoStateInput = Pick<
  DemoState,
  | 'hasClusters'
  | 'hasDiscoveredClusters'
  | 'hasEnabledSecurityAgents'
  | 'isUserTriggeredDemoMode'
>;

const log = makeLogger('Demo State');

// FeatureOpinionatedPlatform is always enabled — cluster demo mode is never active.
// TODO (Justas): Clear demo.state from legacy functionality
const computeIsClusterDemoMode = (_state: DemoStateInput): boolean => false;

const computeIsPureOpDemoMode = (state: DemoStateInput): boolean =>
  state.hasClusters === false && state.hasDiscoveredClusters === false;

const computeIsUserTriggerredOpDemoMode = (state: DemoStateInput): boolean =>
  state.isUserTriggeredDemoMode;

const computeIsOpDemoMode = (state: DemoStateInput): boolean =>
  computeIsPureOpDemoMode(state) || computeIsUserTriggerredOpDemoMode(state);

const computeIsSecurityDemoMode = (state: DemoStateInput): boolean =>
  state.hasEnabledSecurityAgents === false || computeIsOpDemoMode(state);

// TODO (Justas): Add generic logging for zustand https://castai.atlassian.net/browse/UI-5681
export const useDemoState = create(
  subscribeWithSelector(
    persist(
      combine(initialState, (set, get) => ({
        // Setters for raw facts
        setHasClusters: (value: boolean) => {
          log('setHasClusters', value);
          set({ hasClusters: value });
        },
        setHasDiscoveredClusters: (value: boolean) => {
          log('setHasDiscoveredClusters', value);
          set({ hasDiscoveredClusters: value });
        },
        setHasEnabledSecurityAgents: (value: boolean) => {
          log('setHasEnabledSecurityAgents', value);
          set({ hasEnabledSecurityAgents: value });
        },
        setLlmDemoMode: (value: boolean) => {
          log('setLlmDemoMode', value);
          set({ isLlmDemoMode: value });
        },
        setUserTriggeredDemoMode: (value: boolean) => {
          log('setUserTriggeredDemoMode', value);
          set({ isUserTriggeredDemoMode: value });
        },

        // Derived getters (computed from raw facts)
        getIsClusterDemoMode: () => {
          const isClusterDemoMode = computeIsClusterDemoMode(get());
          log('getIsClusterDemoMode', isClusterDemoMode);
          return isClusterDemoMode;
        },
        getIsOpDemoMode: () => {
          const isOpDemoMode = computeIsOpDemoMode(get());
          log('getIsOpDemoMode', isOpDemoMode);
          return isOpDemoMode;
        },

        // Determined when we have clusters OR (we have both cluster results)
        // Truth table (9 states → 2 patterns):
        // hasClusters=true → always determined
        // hasClusters=null → never determined
        // hasClusters=false → determined when hasDiscoveredClusters≠null
        getIsDemoModeDetermined: () => {
          const { hasClusters, hasDiscoveredClusters } = get();
          log('getIsDemoModeDetermined', {
            hasClusters,
            hasDiscoveredClusters,
          });
          return (
            hasClusters === true ||
            (hasClusters === false && hasDiscoveredClusters !== null)
          );
        },

        getIsSecurityDemoMode: () => {
          const isSecurityDemoMode = computeIsSecurityDemoMode(get());
          log('getIsSecurityDemoMode', isSecurityDemoMode);
          return isSecurityDemoMode;
        },

        getIsSecurityDemoModeDetermined: () => {
          const { hasEnabledSecurityAgents } = get();
          const isSecurityDemoModeDetermined =
            hasEnabledSecurityAgents !== null;
          log('getIsSecurityDemoModeDetermined', isSecurityDemoModeDetermined);
          return isSecurityDemoModeDetermined;
        },
      })),
      {
        name: 'demo-state',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          isLlmDemoMode: state.isLlmDemoMode,
          isUserTriggeredDemoMode: state.isUserTriggeredDemoMode,
        }),
      }
    )
  )
);

// Non-reactive getters for middleware
export const getIsClusterDemoMode = () =>
  useDemoState.getState().getIsClusterDemoMode();
export const getIsOpDemoMode = () => useDemoState.getState().getIsOpDemoMode();
/** Classic cluster demo or OP demo org mode (mutations / global gates). */
export const getIsDemoMode = () => getIsClusterDemoMode() || getIsOpDemoMode();
export const getIsSecurityDemoMode = () =>
  useDemoState.getState().getIsSecurityDemoMode();

// Setters for middleware
export const setHasClusters = (value: boolean) =>
  useDemoState.getState().setHasClusters(value);
export const setHasDiscoveredClusters = (value: boolean) =>
  useDemoState.getState().setHasDiscoveredClusters(value);
export const setHasEnabledSecurityAgents = (value: boolean) =>
  useDemoState.getState().setHasEnabledSecurityAgents(value);
export const setUserTriggeredDemoMode = (value: boolean) =>
  useDemoState.getState().setUserTriggeredDemoMode(value);

// Hook selectors for React components (derived)
export const useIsClusterDemoMode = () =>
  useDemoState((s) => s.getIsClusterDemoMode());
export const useIsOpDemoMode = () => useDemoState((s) => s.getIsOpDemoMode());
export const useSimulatedClusterDemoUi = () => {
  const { cluster } = useCluster();
  const isClusterDemoMode = useIsClusterDemoMode();
  const isOpDemoMode = useIsOpDemoMode();

  return (
    isClusterDemoMode ||
    (isOpDemoMode && !!cluster.id && isSimulatedDemoClusterId(cluster.id))
  );
};
export const useIsSecurityDemoMode = () =>
  useDemoState((s) => s.getIsSecurityDemoMode());
export const useIsLlmDemoMode = () => useDemoState((s) => s.isLlmDemoMode);
export const useIsDemoModeDetermined = () =>
  useDemoState((s) => s.getIsDemoModeDetermined());
export const useIsUserTriggeredDemoMode = () =>
  useDemoState((s) => s.isUserTriggeredDemoMode);

// Query invalidation setup
let queryClientRef: QueryClient | null = null;
let unsubscribeRef: (() => void) | null = null;
let unsubscribeAbilityRef: (() => void) | null = null;

export const setupDemoStateQueryInvalidation = (queryClient: QueryClient) => {
  // Cleanup existing subscriptions (for HMR)
  if (unsubscribeRef) {
    log('unsubscribeRef()');
    unsubscribeRef();
  }
  if (unsubscribeAbilityRef) {
    unsubscribeAbilityRef();
  }

  queryClientRef = queryClient;

  unsubscribeRef = useDemoState.subscribe(
    (state) => ({
      hasClusters: state.hasClusters,
      hasDiscoveredClusters: state.hasDiscoveredClusters,
      hasEnabledSecurityAgents: state.hasEnabledSecurityAgents,
      isLlmDemoMode: state.isLlmDemoMode,
      isUserTriggeredDemoMode: state.isUserTriggeredDemoMode,
    }),
    (newState, prevState) => {
      // Only invalidate when derived demo modes change
      // Note: Security demo mode changes are NOT included here to prevent infinite loops.
      // Security pages handle demo mode detection independently via the middleware.
      const hasChanged =
        computeIsOpDemoMode(prevState) !== computeIsOpDemoMode(newState) ||
        prevState.isLlmDemoMode !== newState.isLlmDemoMode;
      log(
        'demo state changed',
        JSON.stringify({ hasChanged, prevState, newState })
      );

      if (hasChanged && queryClientRef) {
        log('invalidateQueries');
        // Skip queries that are:
        // - EXTERNAL_CLUSTER: Set by useClustersQuery, saves an extra request
        // - ORGANIZATIONS: Critical for app init, independent of demo mode, has 24h cache
        queryClientRef.invalidateQueries({
          predicate: ({ queryKey }) =>
            !Array.isArray(queryKey) ||
            (queryKey[0] !== EXTERNAL_CLUSTER_QUERY_KEY &&
              queryKey[0] !== ORGANIZATIONS_QUERY_KEY &&
              queryKey[0] !== ORGANIZATION_SETTINGS_QUERY_KEY),
        });
      }

      const isUserTriggeredDemoModeChanged =
        computeIsUserTriggerredOpDemoMode(prevState) !==
        computeIsUserTriggerredOpDemoMode(newState);

      if (isUserTriggeredDemoModeChanged && queryClientRef) {
        log('invalidateUserTriggeredDemoModeQueries');
        // removing to prevent showing stale data while a new one is fetching
        queryClientRef.removeQueries({
          queryKey: [EXTERNAL_CLUSTERS_QUERY_KEY],
        });
        queryClientRef.removeQueries({
          queryKey: [INVENTORY_ASSET_LIST_CLUSTERS_QUERY_KEY],
        });
      }
    },
    { equalityFn: shallow }
  );

  // On org switch (soft SPA navigation), the clusters API responds before
  // AbilityProvider finishes refetching flags/settings for the new org.
  // The demo middleware sees 0 ability rules, filters out all clusters,
  // and falsely activates demo mode. Invalidate clusters once ability is ready
  // so the middleware re-evaluates with correct permissions.
  // The same scenario exists for late-resolving RBAC data,
  // so the clusters query is invalidated whenever the computed rules change
  // in order for the middleware to re-filter the cluster list with the complete permissions.
  let wasAbilityLoading = false;
  unsubscribeAbilityRef = subscribeAbilityState((state, prevState) => {
    const isLoading = state.ability?.isLoading;

    if (isLoading) {
      wasAbilityLoading = true;
      return;
    }

    const rulesChanged = !isEqual(state.rules, prevState.rules);

    if ((wasAbilityLoading || rulesChanged) && queryClientRef) {
      wasAbilityLoading = false;
      queryClientRef.invalidateQueries({
        queryKey: [EXTERNAL_CLUSTERS_QUERY_KEY],
      });
    }
  });

  return () => {
    unsubscribeRef?.();
    unsubscribeAbilityRef?.();
  };
};
